import {
    Button,
    Card,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTrigger,
    cn,
} from '@repo/shadcn';
import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { TextField } from '@components/form/fields';

import { TCreatePrePurchaseForm } from '@/modules/purchase/pre-purchase/create-pre-purchase/create-pre-purchase.schema';
import {
    calculatePurchaseTotal,
    hasValidPurchaseExpirationDate,
    isPurchaseExpirationDateToday,
} from '@/modules/purchase/shared';
import { formatCurrencyInBDT } from '@/utils';

import { useCreatePrePurchaseContext } from './create-pre-purchase.context';

export const OverviewAndSubmit = () => {
    const { watch, getValues } = useFormContext<TCreatePrePurchaseForm>();
    const { handleSubmitPurchase, isCreatingPurchase, isSaving } =
        useCreatePrePurchaseContext();

    const params = useParams();
    const prePurchaseId = params['id'] || undefined;

    const submitButtonRef = useRef<HTMLButtonElement>(null);
    const totalPaidAmountInputRef = useRef<HTMLInputElement>(null);

    const [isOpen, setIsOpen] = useState(false);
    const [isSaveOpen, setIsSaveOpen] = useState(false);

    const items = watch('items') || [];

    const supplierId = watch('supplier_id');
    const totalPaidAmount = watch('total_paid_amount') || 0;

    const handleChangeToPurchase = () => {
        handleSubmitPurchase(getValues(), prePurchaseId);
    };

    const handleConfirmSave = () => {
        setIsSaveOpen(false);
        submitButtonRef.current?.click();
    };

    const totalPurchasePrice = calculatePurchaseTotal(
        items.flatMap((item) =>
            item.purchase_price !== undefined && item.quantity !== undefined
                ? [
                      {
                          purchase_price: item.purchase_price,
                          quantity: item.quantity,
                      },
                  ]
                : [],
        ),
    );

    const totalDue = totalPurchasePrice - Math.round(totalPaidAmount);
    const expiringTodayCount = items.filter((item) =>
        isPurchaseExpirationDateToday(item.expiration_date),
    ).length;

    const renderExpirationWarning = () => {
        if (expiringTodayCount === 0) {
            return null;
        }

        return (
            <DialogDescription className={'font-medium text-amber-700'}>
                Warning: {expiringTodayCount}{' '}
                {expiringTodayCount === 1 ? 'item expires' : 'items expire'}
                {' today.'}
            </DialogDescription>
        );
    };

    const renderTotal = () => {
        return (
            <div className="flex flex-col items-end justify-center">
                <p className={'text-nowrap border-r-[1px] border-black pr-2'}>
                    Total Price:{' '}
                    <strong className={''}>
                        {formatCurrencyInBDT(totalPurchasePrice)}
                    </strong>
                </p>

                <small
                    className={cn(
                        'text-nowrap border-r-[1px] border-black pr-2',
                        totalDue > 0 ? 'text-red-600' : 'text-green-600',
                    )}
                >
                    Due Amount:{' '}
                    <strong className={''}>
                        {formatCurrencyInBDT(totalDue)}
                    </strong>
                </small>
            </div>
        );
    };

    const isDisabled = items.length === 0 || isCreatingPurchase || isSaving;
    const isPurchaseDisabled =
        !supplierId ||
        items.length === 0 ||
        items.some(
            (item) => !hasValidPurchaseExpirationDate(item.expiration_date),
        ) ||
        isCreatingPurchase ||
        isSaving;

    return (
        <Card
            className={
                'absolute bottom-0 right-0 col-span-2 flex w-full flex-row items-center justify-between gap-4 p-2'
            }
        >
            <div className={'flex flex-row gap-2'}>
                <p className={'text-nowrap'}>
                    Total Items:{' '}
                    <strong className={''}>{items?.length || 0}</strong>
                </p>
            </div>

            <div className={'flex items-center justify-end gap-2'}>
                {renderTotal()}
                <div className={'text-nowrap'}>Paid Amount:</div>
                <TextField<TCreatePrePurchaseForm>
                    name={'total_paid_amount'}
                    placeholder={'Enter paid amount'}
                    inputProps={{
                        onClick: () => {
                            if (totalPaidAmountInputRef.current) {
                                totalPaidAmountInputRef.current.select();
                            }
                        },
                    }}
                    ref={totalPaidAmountInputRef}
                    className={'w-full text-right'}
                    inputClass={'text-right ring-0'}
                    // label={'Paid Amount'}
                />

                <Dialog open={isOpen} onOpenChange={setIsOpen}>
                    <DialogTrigger asChild>
                        <Button
                            type={'button'}
                            variant={'outline'}
                            disabled={isPurchaseDisabled}
                            title={
                                items.some(
                                    (item) =>
                                        !hasValidPurchaseExpirationDate(
                                            item.expiration_date,
                                        ),
                                )
                                    ? 'Every item needs an expiration date of today or later before purchase.'
                                    : undefined
                            }
                        >
                            Purchase
                        </Button>
                    </DialogTrigger>

                    <DialogContent>
                        <DialogHeader>
                            <h3 className={'text-lg font-bold'}>
                                Are you sure you want to convert this to
                                {renderExpirationWarning()}
                                purchase?
                            </h3>
                        </DialogHeader>

                        <div className="flex flex-col items-start justify-center">
                            <p className={'text-nowrap'}>
                                Total Price:{' '}
                                <strong className={''}>
                                    {formatCurrencyInBDT(totalPurchasePrice)}
                                </strong>
                            </p>

                            <small
                                className={cn(
                                    'text-nowrap',
                                    totalDue > 0
                                        ? 'text-red-600'
                                        : 'text-green-600',
                                )}
                            >
                                Due Amount:{' '}
                                <strong className={''}>
                                    {formatCurrencyInBDT(totalDue)}
                                </strong>
                            </small>
                        </div>

                        <div className={'mt-4 flex justify-end gap-2'}>
                            <Button
                                className={''}
                                type={'button'}
                                variant={'outline'}
                                onClick={() => setIsOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type={'button'}
                                disabled={isCreatingPurchase}
                                onClick={handleChangeToPurchase}
                            >
                                {isCreatingPurchase
                                    ? 'Processing...'
                                    : 'Confirm'}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>

                <Dialog open={isSaveOpen} onOpenChange={setIsSaveOpen}>
                    <DialogTrigger asChild>
                        <Button
                            disabled={isDisabled}
                            type={'button'}
                            variant={'default'}
                        >
                            Save
                        </Button>
                    </DialogTrigger>

                    <DialogContent>
                        <DialogHeader>
                            <h3 className={'text-lg font-bold'}>
                                {renderExpirationWarning()}
                                Are you sure you want to save this pre purchase?
                            </h3>
                        </DialogHeader>

                        <div className={'mt-4 flex justify-end gap-2'}>
                            <Button
                                type={'button'}
                                variant={'outline'}
                                onClick={() => setIsSaveOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button type={'button'} onClick={handleConfirmSave}>
                                Confirm
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            <button
                className={'hidden'}
                ref={submitButtonRef}
                type={'submit'}
            />
        </Card>
    );
};

OverviewAndSubmit.displayName = 'OverviewAndSubmit';
